import type {LegendOverrides} from 'common/types/reports/bookmark';

/**
 * For a given series path, returns the portion of the
 * path that a match would be tested on
 *
 * @example 'A. Viewed Report, USA' => 'USA'
 * @example 'A. Viewed Report, USA, Chrome' => 'USA, Chrome'
 * @example 'A. Viewed Report' => 'Viewed Report'
 */
export function getSeriesMatchPath(
  id: string,
  {includeMetricName = true}: {includeMetricName?: boolean} = {},
): string | null {
  const parts = id.split(`,`);

  if (parts.length === 1) {
    if (!includeMetricName) {
      return null;
    }

    // matches metric names in the form of "A. Viewed Report, AB. Purchase, etc.."
    const metricNameMatches = parts[0].match(/^([A-Z]+\.) (.+)/);
    return metricNameMatches ? metricNameMatches[2] : parts[0];
  }

  return parts.slice(1).join(`,`);
}

/**
 * Merges legend overrides making sure incoming overrides
 * correctly overwrite matching segments
 * @example
 * mergeLegendOverrides(
 *   // bookmark overrides
 *   {"A. Viewed Report, USA": {"color": "red"},
 *   // global overrides
 *   {"USA": {"color": "blue"}}
 *)
 * Results in `USA` being colored `blue`
 */
export function mergeLegendOverrides(source: LegendOverrides, overrides: LegendOverrides) {
  source = {...source};
  for (const bookmarkOverrideKey of Object.keys(source)) {
    const matchPath = getSeriesMatchPath(bookmarkOverrideKey);

    if (matchPath && overrides && matchPath in overrides) {
      delete source[bookmarkOverrideKey];
    }
  }

  Object.assign(source, overrides);
  return source;
}
